import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { RootState } from 'store/rootReducer';
import {
  clearSearchBebyggelseobjektResult,
  clearSearchCriteria,
  searchBebyggelseobjekt
} from 'store/searchBebyggelseobjektSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';

import { RaaSelect, RaaSelectControlled, RaaSpinner } from 'raa-react';

import {
  DrawerBody,
  DrawerContainer,
  DrawerSection,
  TabNavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import InputField from 'components/fieldComponents/raaInputField/RaaInputField';
import SearchFooter from 'components/search/SearchFooter';
import {
  SearchBebyggelseFormValues,
  searchBebyggelseobjektSchema
} from 'components/search/bebyggelseobjekt/searchBebyggelseobjektSchema';
import { LanKommunSelectWithTagList, buildQueryString, getLanAndKommunValues } from 'components/search/common';
import TagList from 'components/tagList/TagList';

import { Skyddstyp } from 'models';

import { DebouncedSearchTrigger } from './DebouncedSearchTrigger';

import { useLazySearchLmFastighetsbeteckningQuery } from 'api/endpoints/LMSearchFastighetSlice';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';

const SearchBebyggelseobjektForm: FC = () => {
  const [initialValues, setInitialValues] = useState<SearchBebyggelseFormValues>({
    bebyggelsenummer: '',
    namn: '',
    beteckning: '',
    skyddstyp: [],
    kommun_kod: [],
    lan_kod: []
  });
  const [isSearchingByBebyggelsenummer, setIsSearchingByBebyggelsenummer] = useState<boolean>(false);
  const [bebyggelseobjektNotFound, setBebyggelseobjektNotFound] = useState<boolean>(false);

  const [fastighetOptions, setFastighetOptions] = useState<Array<string>>([]);
  const [fastighetSearchIsCleared, setFastighetSearchIsCleared] = useState(false);
  const [isIncompleteFastighetsbeteckning, setIsIsIncompleteFastighetsbeteckning] = useState(false);

  const [searchLmFastighetsbeteckning, { isFetching: searchFastighetsbeteckningIsFetching }] =
    useLazySearchLmFastighetsbeteckningQuery();

  const formikRef = useRef<FormikProps<SearchBebyggelseFormValues>>(null);

  const { isAnonymous } = useAppSelector((state: RootState) => state.user);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    requestStatus: bebyggelseobjektRequestStatus,
    data: searchedBebyggelseobjekt,
    searchCriteria
  } = useAppSelector((state: RootState) => state.searchBebyggelseobjekt);

  const { requestStatus: domanRequestStatus, data: domaner } = useAppSelector(
    (state: RootState) => state.bebyggelseobjektDomaner
  );
  const { requestStatus: geoRequestStatus, data: geo } = useAppSelector((state: RootState) => state.geo);
  const { requestStatus: utforareRequestStatus } = useAppSelector((state: RootState) => state.utforare);

  const { lanItems, kommunItems } = useMemo(() => getLanAndKommunValues(geo.kommun, geo.lan), [geo.kommun, geo.lan]);

  const formIsEmpty = (values: SearchBebyggelseFormValues) =>
    !values.bebyggelsenummer.length &&
    !values.beteckning.length &&
    !values.lan_kod.length &&
    !values.kommun_kod.length &&
    !values.namn.length &&
    !values.skyddstyp.length;

  const handleSubmit = async (values: SearchBebyggelseFormValues): Promise<void> => {
    const isIncompleteFastighetsbeteckning =
      values.beteckning.length > 0 && !isFinalSelection(values.beteckning, fastighetOptions);

    if (formIsEmpty(values) || isIncompleteFastighetsbeteckning) return;

    if (values.bebyggelsenummer.length > 0) {
      dispatch(searchBebyggelseobjekt({ criteria: { bebyggelsenummer: [values.bebyggelsenummer] } }));
    } else {
      const queryString = buildQueryString(values);
      navigate(`/sok-bebyggelseobjekt/resultat?${queryString}&page=0`);
    }
  };

  const handleSearchFastigheterInputClear = () => {
    setFastighetSearchIsCleared(true);
    void formikRef.current?.setFieldValue('beteckning', '');
    setFastighetOptions([]);
  };

  const clearForm = useCallback(() => {
    dispatch(clearSearchCriteria());
    setIsSearchingByBebyggelsenummer(false);
    setBebyggelseobjektNotFound(false);
    setFastighetSearchIsCleared(true);
    setFastighetOptions([]);
    formikRef.current?.resetForm();
  }, [dispatch]);

  const searchFastigheter = useCallback(
    async (query: string) => {
      if (!query || (searchCriteria?.beteckning && searchCriteria.beteckning === query)) {
        setFastighetOptions([]);
        return;
      }

      setFastighetOptions([]);
      const { data, isError, error } = await searchLmFastighetsbeteckning(
        {
          beteckning: query,
          maxhits: 20,
          objektstatus: 'levande',
          status: 'gällande'
        },
        true
      );

      if (data) {
        setFastighetOptions(data.searchResultBeteckning.results);
      } else if (isError) {
        console.error('Fel vid hämtning av fastighetsbeteckningar:', error);
        setFastighetOptions([]);
      }
    },
    [searchCriteria?.beteckning, searchLmFastighetsbeteckning]
  );

  const isFinalSelection = <T,>(val: T, options: Array<T>, displayAttr?: keyof T): boolean => {
    if (val == null) {
      return false;
    }

    if (searchCriteria?.beteckning && searchCriteria.beteckning === val) {
      return true;
    }

    let text: string;

    if (typeof val === 'string') {
      text = val;
    } else if (displayAttr) {
      const extracted = (val as any)[displayAttr];
      if (typeof extracted !== 'string') return false;
      text = extracted;
    } else {
      return false;
    }

    const hasDigit = /\d/.test(text);

    const match = options.some((opt) => {
      if (typeof opt === 'string') {
        return opt.trim().toLowerCase() === text.trim().toLowerCase();
      } else if (displayAttr) {
        const optionText = (opt as any)[displayAttr];
        return typeof optionText === 'string' && optionText.trim().toLowerCase() === text.trim().toLowerCase();
      }
      return false;
    });

    return hasDigit && match;
  };

  useEffect(() => {
    if (searchCriteria) {
      setInitialValues({
        bebyggelsenummer:
          searchCriteria.bebyggelsenummer && searchCriteria.bebyggelsenummer.length
            ? searchCriteria.bebyggelsenummer[0]
            : '',
        namn: searchCriteria.namn || '',
        beteckning: searchCriteria.beteckning || '',
        skyddstyp: searchCriteria?.skyddstyper || [],
        kommun_kod: searchCriteria.kommun_kod || [],
        lan_kod: searchCriteria.lan_kod || []
      });
    } else {
      setInitialValues({
        bebyggelsenummer: '',
        namn: '',
        beteckning: '',
        skyddstyp: [],
        kommun_kod: [],
        lan_kod: []
      });
    }
  }, [domaner?.skyddstyper, kommunItems, lanItems, searchCriteria]);

  useEffect(() => {
    if (formikRef.current?.values.bebyggelsenummer.length && searchedBebyggelseobjekt?.results.length === 1) {
      dispatch(clearSearchBebyggelseobjektResult());
      navigate(`/bebyggelseobjekt/${searchedBebyggelseobjekt?.results[0].id}`);
    } else if (searchedBebyggelseobjekt?.results.length === 0) {
      setBebyggelseobjektNotFound(true);
    }
  }, [searchedBebyggelseobjekt, formikRef.current?.values.bebyggelsenummer, dispatch, navigate]);

  useEffect(() => {
    setBebyggelseobjektNotFound(false);
  }, [formikRef.current?.values.bebyggelsenummer]);

  return (
    <>
      {initialValues && (
        <DrawerContainer>
          <TabNavigationDrawerHead />
          <DrawerBody>
            <DrawerSection>
              <h1 className="tw-heading-md tw-mb-2">Sök bebyggelseobjekt</h1>
              {domanRequestStatus === 'pending' ||
              utforareRequestStatus === 'pending' ||
              geoRequestStatus === 'pending' ? (
                <div className="tw-flex tw-justify-center tw-py-8">
                  <RaaSpinner size="3rem" />
                </div>
              ) : (
                <Formik
                  onSubmit={handleSubmit}
                  validationSchema={searchBebyggelseobjektSchema}
                  enableReinitialize={true}
                  initialValues={initialValues}
                  innerRef={formikRef}
                >
                  {({ errors, touched, values, setFieldValue, submitCount }) => (
                    <Form id="searchForm" className="tw-flex tw-h-full tw-flex-col tw-gap-4">
                      <section className="tw-border-b tw-border-raa-gray-3">
                        <Bebyggelsenummer
                          formikHelpers={formikRef.current}
                          value={values.bebyggelsenummer}
                          hasValue={setIsSearchingByBebyggelsenummer}
                        />
                        <p className="tw-h-6 tw-text-raa-error-red">
                          {values.bebyggelsenummer.length > 0 &&
                            errors.bebyggelsenummer &&
                            touched.bebyggelsenummer &&
                            errors.bebyggelsenummer}
                          {bebyggelseobjektNotFound && 'Inget bebyggelseobjekt hittades'}
                        </p>
                      </section>

                      <fieldset
                        disabled={isSearchingByBebyggelsenummer}
                        className={classNames('tw-flex tw-flex-col tw-gap-4', {
                          'tw-pointer-events-none tw-opacity-25': isSearchingByBebyggelsenummer
                        })}
                      >
                        <legend className="tw-sr-only">Ange sökparametrar för sökning på bebyggelseobjekt</legend>

                        <section>
                          <InputField
                            name="namn"
                            label="Namn"
                            placeholder="Sök efter bebyggelseobjektets namn"
                            disabled={isSearchingByBebyggelsenummer}
                          />
                        </section>

                        {!isAnonymous && (
                          <section>
                            {/*Denna för att ha tillgång till formulärets kontext då den vi har sparat som ref inte går att lita på*/}
                            <DebouncedSearchTrigger
                              searchFastigheter={searchFastigheter}
                              setIsIncompleteFastighetsbeteckning={setIsIsIncompleteFastighetsbeteckning}
                              fastighetOptions={fastighetOptions}
                              isFinalSelection={isFinalSelection}
                              setIsCleared={setFastighetSearchIsCleared}
                              isCleared={fastighetSearchIsCleared}
                            />
                            <label htmlFor="fastighet" className="raa-label">
                              Fastighet som berörs (KOMMUN TRAKT NR)
                            </label>
                            <RaaSelectControlled
                              inputElementId="beteckning"
                              name="beteckning"
                              placeholder="T.ex. FALUN RANKHYTTAN 3:3"
                              options={fastighetOptions}
                              isLoading={searchFastighetsbeteckningIsFetching}
                              value={values.beteckning}
                              onSelect={async (beteckning) => void setFieldValue('beteckning', beteckning)}
                              onChange={(value) => void setFieldValue('beteckning', value)}
                              onClear={handleSearchFastigheterInputClear}
                              isFinalSelection={isFinalSelection}
                              addEndingCharToSearchQueryOnSelection={(value) => {
                                const endsWithDash = /-$/.test(value);
                                const endsWithFastighetbeteckningFormat = /\d+(:\d+)?(-\d+)?$/.test(value.trim());

                                if (!endsWithFastighetbeteckningFormat && !endsWithDash) {
                                  return ' ';
                                }

                                return null;
                              }}
                            />

                            {submitCount > 0 && isIncompleteFastighetsbeteckning && (
                              <p className="tw-text-raa-error-red">Ofullständig fastighetsbeteckning</p>
                            )}
                          </section>
                        )}

                        <section>
                          <SkyddstyperWithTagList
                            skyddstyper={domaner?.skyddstyper}
                            disabled={isSearchingByBebyggelsenummer}
                          />
                        </section>
                        <section>
                          <LanKommunSelectWithTagList disabled={isSearchingByBebyggelsenummer} />
                        </section>
                      </fieldset>
                    </Form>
                  )}
                </Formik>
              )}
            </DrawerSection>
          </DrawerBody>
          <SearchFooter
            domanRequestStatus={domanRequestStatus}
            searchRequestStatus={bebyggelseobjektRequestStatus}
            clearForm={clearForm}
          />
        </DrawerContainer>
      )}
    </>
  );
};

const Bebyggelsenummer: FC<{
  formikHelpers: FormikHelpers<SearchBebyggelseFormValues> | null;
  value: string;
  hasValue: Dispatch<SetStateAction<boolean>>;
}> = ({ formikHelpers, value, hasValue }) => {
  const { setFieldValue } = useFormikContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const valueChanged = async (text: string) => {
    const formatted = formatBebyggelseobjektnummer(text, value);
    let cursorPosition = inputRef.current && inputRef.current.selectionStart;
    if (((cursorPosition === 1 && text !== 'B') || cursorPosition === 6) && text.length > value.length) {
      cursorPosition += 1;
    }
    await formikHelpers?.setFieldValue('bebyggelsenummer', formatted);
    await setFieldValue('bebyggelsenummer', formatted);
    inputRef.current && inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    hasValue(text !== '');
  };

  const formatBebyggelseobjektnummer = (newValue: string, previousValue: string): string => {
    if (!newValue) return newValue;

    if (!previousValue || newValue.length > previousValue.length) {
      let result = '';

      for (let i = 0; i < newValue.length; i++) {
        const char = newValue[i];

        if (i == 0 && char === 'b') {
          result = 'B';
        } else if (i === 0 && char !== 'B') {
          result += 'B' + char;
        } else if (i === 5) {
          result += char === ':' ? char : ':' + char;
        } else if (char !== ':') {
          result += char;
        }
      }
      return result;
    }
    return newValue;
  };

  return (
    <div className="tw-mb-2 tw-w-1/2">
      <label className="raa-label form-label tw-flex tw-items-center" htmlFor="bebyggelsenummer">
        Bebyggelsenummer
      </label>
      <input
        id="bebyggelsenummer"
        name="text-input"
        className="raa-input"
        type="text"
        placeholder="BÅÅÅÅ:N"
        value={value}
        ref={inputRef}
        onChange={(e) => valueChanged(e.target.value)}
      />
    </div>
  );
};

const SkyddstyperWithTagList: FC<{
  skyddstyper?: Skyddstyp[];
  disabled: boolean;
}> = ({ skyddstyper, disabled }) => {
  const { setFieldValue, values } = useFormikContext<SearchBebyggelseFormValues>();
  const [currentSkyddstyper, setCurrentSkyddstyper] = useState<Array<Skyddstyp>>(skyddstyper || []);

  const { data: domaner } = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner);

  const toSkyddstyp = useCallback(
    (skyddstypId: string) => {
      const fullSkyddstyp = domaner?.skyddstyper?.find((skyddstyp) => skyddstyp.skyddstyp === skyddstypId);

      if (!fullSkyddstyp) throw new Error('Hittade inte skyddstyp med id: ' + skyddstypId);

      return fullSkyddstyp;
    },
    [domaner?.skyddstyper]
  );

  useEffect(() => {
    if (skyddstyper) {
      setCurrentSkyddstyper(skyddstyper.filter((skyddsTyp) => !values.skyddstyp.includes(skyddsTyp.skyddstyp)));
    }
  }, [skyddstyper, values]);

  return (
    <>
      {skyddstyper && (
        <>
          <label className="raa-label" htmlFor="skyddstyper">
            Skyddstyp
          </label>
          <RaaSelect
            clearInputOnSelection={true}
            inputElementId="skyddstyper"
            domain={currentSkyddstyper}
            displayAttr={'namn'}
            disabled={disabled}
            onSelect={async (skyddsTyp: Skyddstyp) => {
              if (!values.skyddstyp.includes(skyddsTyp.skyddstyp)) {
                await setFieldValue('skyddstyp', [
                  ...values.skyddstyp.map((skyddstyp) => skyddstyp),
                  skyddsTyp.skyddstyp
                ]);
              }
            }}
          />
          {
            <div className="tw-mt-2">
              <TagList
                list={values.skyddstyp.map(toSkyddstyp)}
                leftColumn={(skyddstyp: Skyddstyp) => skyddstyp.namn}
                onRemoveItem={async (toRemove: Skyddstyp) =>
                  await setFieldValue(
                    'skyddstyp',
                    values.skyddstyp
                      .filter((skyddstyp: string) => skyddstyp !== toRemove.skyddstyp)
                      .map((skyddstyp: string) => skyddstyp)
                  )
                }
              />
            </div>
          }
        </>
      )}
    </>
  );
};

export default SearchBebyggelseobjektForm;
